import { useState, useEffect, useRef } from "react";
import FormSearch from "@/components/FormSearch";
import { useNotifications } from "@/context/NotificationsContext";
import { useAppDispatch, useAppSelector } from "@/redux/features/hooks";
import { Link } from "react-router-dom";
import { Roles } from "@/redux/services/user/user.type";
import { useLazyGetRestaurantsQuery } from "@/redux/services/restaurant/restaurant.api";
import { useGetOpenSessionQuery } from "@/redux/services/pos/registerSession.api";
import { setCurrentRestaurant } from "@/redux/features/user/user.slice";
import { Restaurant } from "@/redux/services/restaurant/restaurant.type";
import { getProfilePictureUrl, getInitials, isGranted, cn } from "@/lib/utils";
import { ConnectionStatusIndicator } from "@/components/ConnectionStatusIndicator";
import { DebugPanel } from "@/components/DebugPanel";
import { useDebugGesture } from "@/hooks/useDebugGesture";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import NotificationPanel from "@/components/Notifications";
import FranchiseSwitcher from "@/layouts/FranchiseSwitcher";
import { Bell, Menu, X, LayoutGrid, Check, Home } from "lucide-react";

const getLogoUrl = (imageUrl?: string) => {
  if (imageUrl && imageUrl.trim() !== "") {
    return getProfilePictureUrl(imageUrl);
  }
  return "/images/icon.png";
};

const roleLabels: Record<string, string> = {
  [Roles.owner]: "Propriétaire",
  [Roles.manager]: "Manager",
  [Roles.waiter]: "Serveur",
  [Roles.kitchen]: "Cuisine",
  [Roles.cashier]: "Caissier",
  [Roles.admin]: "Admin",
  [Roles.support]: "Support",
  [Roles.staff]: "Staff",
};

const getRoleLabel = (roles?: string[]) => {
  const role = roles?.find((r) => r !== Roles.user);
  return role ? (roleLabels[role] ?? role) : "";
};

export function IsShowTopbar() {
  return true;
}

const MAX_LINE_LENGTH = 14;

const RestaurantName = ({ name }: { name: string }) => {
  const words = name.split(" ");
  let firstLine = "";
  let secondLine = "";

  for (const word of words) {
    if (!firstLine || firstLine.length + word.length + 1 <= MAX_LINE_LENGTH) {
      firstLine = firstLine ? `${firstLine} ${word}` : word;
    } else {
      secondLine = secondLine ? `${secondLine} ${word}` : word;
    }
  }

  if (!secondLine) {
    return (
      <h1 className="font-semibold text-xl leading-tight text-card-foreground">
        {firstLine}
      </h1>
    );
  }

  return (
    <h1 className="flex flex-col leading-tight">
      <span className="font-semibold text-[18px] text-card-foreground">
        {firstLine}
      </span>
      <span className="text-[14px] text-muted-foreground font-medium">
        {secondLine}
      </span>
    </h1>
  );
};

const TopBar: React.FC<{ isMenuOpen: boolean; toggleMenu: () => void }> = ({
  isMenuOpen,
  toggleMenu,
}) => {
  const { unreadCount } = useNotifications();
  const { currentUser, currentRestaurant, currentFranchise } = useAppSelector(
    (state) => state.user
  );
  const isOwner = currentUser?.roles?.includes(Roles.owner);
  const dispatch = useAppDispatch();
  const [fetch, { data: restaurants }] = useLazyGetRestaurantsQuery();

  const showRegisterIndicator = isGranted(
    [Roles.manager, Roles.cashier, Roles.waiter],
    currentUser?.roles
  );
  const { data: openSessionData } = useGetOpenSessionQuery(
    { restaurantId: currentRestaurant?.id ?? "" },
    { skip: !currentRestaurant?.id || !showRegisterIndicator }
  );
  const hasOpenSession = !!openSessionData;

  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [isRestaurantSelectorOpen, setIsRestaurantSelectorOpen] =
    useState(false);
  const restaurantSelectorRef = useRef<HTMLDivElement>(null);

  const { isDebugOpen, closeDebug } = useDebugGesture({
    targetElement: "h1",
    tapCount: 3,
    tapWindow: 1000,
  });

  // GET /restaurants is scoped by the API to the selected franchise
  // (X-Franchise-Id, SFRESTO-169), so the list only shows its restaurants.
  useEffect(() => {
    if (currentUser?.roles?.includes(Roles.owner)) {
      fetch();
    }
  }, [currentFranchise?.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        restaurantSelectorRef.current &&
        !restaurantSelectorRef.current.contains(event.target as Node)
      ) {
        setIsRestaurantSelectorOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!IsShowTopbar()) {
    return null;
  }

  const handleSelectRestaurant = (restaurant: Restaurant | null) => {
    if (restaurant) {
      dispatch(setCurrentRestaurant(restaurant));
    } else {
      dispatch(setCurrentRestaurant({} as Restaurant));
    }
    setIsRestaurantSelectorOpen(false);
  };

  const displayName = isOwner
    ? (currentRestaurant?.name ?? currentFranchise?.name)
    : currentRestaurant?.name;

  return (
    <>
      <header className="bg-white border-b border-border w-full z-20 sticky top-0">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3 flex-shrink-0">
              <img
                src={getLogoUrl(currentRestaurant?.image?.url)}
                alt={displayName}
                className="w-10 h-10 rounded-lg object-cover"
              />
              <span className="hidden sm:inline">
                <RestaurantName name={displayName ?? "ScaniFood"} />
              </span>
            </Link>

            {/* Connection Status Indicator - desktop */}
            {isGranted([Roles.waiter, Roles.cashier], currentUser?.roles) && (
              <div className="hidden lg:block">
                <ConnectionStatusIndicator />
              </div>
            )}

            {isGranted([Roles.manager, Roles.owner], currentUser?.roles) && (
              <div className="relative w-full max-w-lg hidden lg:block">
                <FormSearch placeholder="Rechercher un menu, une commande, et plus" />
              </div>
            )}

            {/* Right: Notification bell + Restaurant selector + User profile */}
            <div className="flex items-center gap-2">
              {/* Connection Status Indicator - mobile */}
              {isGranted([Roles.waiter, Roles.cashier], currentUser?.roles) && (
                <div className="lg:hidden">
                  <ConnectionStatusIndicator />
                </div>
              )}

              {/* Register session indicator */}
              {showRegisterIndicator && (
                <Link
                  to="/register"
                  className={cn(
                    "hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors",
                    hasOpenSession
                      ? "bg-green-50 text-green-700 hover:bg-green-100"
                      : "bg-red-50 text-red-700 hover:bg-red-100"
                  )}
                >
                  <span
                    className={cn(
                      "h-2 w-2 rounded-full",
                      hasOpenSession ? "bg-green-500" : "bg-red-500"
                    )}
                  />
                  {hasOpenSession ? "Caisse ouverte" : "Caisse fermée"}
                </Link>
              )}

              {/* Home shortcut — opens the floor hub for the roles that have
                  one (manager/cashier/waiter); owners fall back to their
                  landing page. Kitchen staff have no hub, so they don't get
                  the shortcut. */}
              {!currentUser?.roles?.includes(Roles.kitchen) && (
                <Link
                  to={
                    isGranted(
                      [Roles.manager, Roles.cashier, Roles.waiter],
                      currentUser?.roles
                    )
                      ? "/hub"
                      : "/"
                  }
                  className="p-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors flex"
                  aria-label="Accueil"
                  title="Accueil"
                >
                  <Home className="h-5 w-5" />
                </Link>
              )}

              {/* Notification bell */}
              <button
                onClick={() => setIsNotificationPanelOpen(true)}
                className="relative p-2 rounded-lg text-muted-foreground transition-colors flex"
                aria-label="Notifications"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 bg-primary text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>

              {/* Franchise switcher (owners of 2+ franchises) */}
              <FranchiseSwitcher />

              {/* Restaurant selector (owners only) - Google-style grid */}
              {isOwner && (
                <div
                  className="relative hidden lg:block"
                  ref={restaurantSelectorRef}
                >
                  <button
                    onClick={() =>
                      setIsRestaurantSelectorOpen(!isRestaurantSelectorOpen)
                    }
                    className="p-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
                    aria-label="Sélectionner un restaurant"
                  >
                    <LayoutGrid className="h-5 w-5" />
                  </button>

                  {isRestaurantSelectorOpen && (
                    <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-border py-2 z-50">
                      <p className="text-sm font-medium text-muted-foreground px-4 py-2">
                        Vos restaurants
                      </p>
                      <div className="flex flex-col max-h-80 overflow-y-auto">
                        {/* All restaurants option */}
                        <button
                          onClick={() => handleSelectRestaurant(null)}
                          className={cn(
                            "flex items-center gap-3 px-4 py-2.5 transition-colors text-left",
                            currentRestaurant?.id
                              ? "hover:bg-muted"
                              : "bg-primary-light"
                          )}
                        >
                          <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                            <LayoutGrid className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <span className="text-sm font-medium flex-1">
                            Tous les restaurants
                          </span>
                          {!currentRestaurant?.id && (
                            <Check className="h-4 w-4 text-primary flex-shrink-0" />
                          )}
                        </button>

                        {restaurants?.map((restaurant) => (
                          <button
                            key={restaurant.id}
                            onClick={() => handleSelectRestaurant(restaurant)}
                            className={cn(
                              "flex items-center gap-3 px-4 py-2.5 transition-colors text-left",
                              currentRestaurant?.id === restaurant.id
                                ? "bg-primary-light"
                                : "hover:bg-muted"
                            )}
                          >
                            {restaurant.image?.url ? (
                              <img
                                src={getProfilePictureUrl(restaurant.image.url)}
                                alt={restaurant.name}
                                className="w-9 h-9 rounded-lg object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-9 h-9 rounded-lg bg-primary-light flex items-center justify-center flex-shrink-0">
                                <span className="text-primary font-semibold text-sm">
                                  {restaurant.name?.charAt(0)?.toUpperCase()}
                                </span>
                              </div>
                            )}
                            <span className="text-sm font-medium flex-1">
                              {restaurant.name}
                            </span>
                            {currentRestaurant?.id === restaurant.id && (
                              <Check className="h-4 w-4 text-primary flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* User profile */}
              <Link
                to="/profile"
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors"
              >
                <Avatar className="h-9 w-9">
                  <AvatarImage
                    src={getProfilePictureUrl(currentUser?.image?.url)}
                    alt="profil"
                    className="object-cover object-top"
                  />
                  <AvatarFallback className="bg-primary-light text-primary text-xs font-semibold">
                    {getInitials(currentUser?.fullName)}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden xl:flex flex-col leading-tight max-w-[120px]">
                  <span className="text-sm font-semibold text-card-foreground truncate">
                    {currentUser?.fullName}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {getRoleLabel(currentUser?.roles)}
                  </span>
                </div>
              </Link>

              {/* Mobile hamburger */}
              <button
                onClick={toggleMenu}
                className="relative p-2 rounded-lg xl:hidden"
                aria-label={isMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6 text-card-foreground" />
                ) : (
                  <Menu className="h-6 w-6 text-card-foreground" />
                )}
              </button>
            </div>
          </div>

          {/* Search bar - mobile */}
          {isGranted([Roles.manager, Roles.owner], currentUser?.roles) && (
            <div className="lg:hidden mt-3">
              <FormSearch placeholder="Rechercher un menu, une commande, ..." />
            </div>
          )}
        </div>

        <DebugPanel isOpen={isDebugOpen} onClose={closeDebug} />
      </header>

      {/* Notification Panel */}
      <NotificationPanel
        isOpen={isNotificationPanelOpen}
        onClose={() => setIsNotificationPanelOpen(false)}
      />
      {isNotificationPanelOpen && (
        <button
          className="fixed inset-0 bg-black/50 z-40 cursor-default"
          onClick={() => setIsNotificationPanelOpen(false)}
          aria-label="Close notification panel"
        />
      )}
    </>
  );
};

export default TopBar;
